/* The durable upload queue (Story 8.2, UX-DR-19, AD-18, NFR-6).
 *
 * An Operations user photographs a reload at a bonded warehouse where the
 * signal is one bar and the building is concrete. The work this module exists
 * to protect is not the upload — it is the user's CONFIDENCE that the upload
 * will happen. Losing eight photos silently is bad; losing them while the
 * interface showed nothing pending is what makes somebody drive back.
 *
 * IndexedDB, not localStorage, and this is the decision the whole module turns
 * on. The queue must hold the BYTES: a queue that survived a refresh but lost
 * the file would restore a list of names nobody can send, which is worse than
 * an empty queue because it looks like progress. localStorage stores strings
 * only — a File would have to be base64'd into one, inflating a 4 MiB photo to
 * ~5.5 MiB against a 5 MiB origin quota, and eight of them would exceed it on
 * the first reload. IndexedDB stores a File/Blob natively, has no practical
 * size ceiling at this scale, and its writes do not block the main thread while
 * a user is still shooting.
 *
 * Not the Background Sync API, though it is the purpose-built answer: it needs
 * a service worker, it is Chromium-only (Safari and Firefox have both declined
 * it), and Operations are on whatever phone they own. A queue that works on
 * half the devices is a queue nobody can be told to trust.
 *
 * Retention is SEVEN DAYS, from the AC. Not a cache TTL — an expiry that says
 * how long we promise the work is safe. See sweepExpired for why the sweep
 * refuses to run on entries that are merely old rather than expired.
 */

/** How long a queued upload is kept before it is abandoned (the AC's floor).
 *
 *  Seven days rather than "forever" because a File pinned in IndexedDB is disk
 *  the user cannot see or reclaim, and an upload for a case closed months ago
 *  is not work anybody still wants sent. Seven days is long enough to cover a
 *  driver on a week-long run with no roaming, which is the case the AC has in
 *  mind. */
export const QUEUE_RETENTION_MS = 7 * 24 * 60 * 60 * 1000

const DB_NAME = 'asa-upload-queue'
const DB_VERSION = 1
const STORE = 'uploads'

/** Index name for the per-case read. Declared once: the upgrade that creates it
 *  and the read that opens it must agree, and two spellings fail only at
 *  runtime, on a device, in a warehouse. */
const BY_CASE = 'byCase'

/** One queued upload, exactly as it is persisted.
 *
 *  The File itself is a field. Structured-clone stores it whole — bytes,
 *  name and type — so a restored entry is the same upload rather than a
 *  description of one. */
export interface QueuedUpload {
  /* The idempotency key, and simultaneously this record's primary key (AD-18).
   *
   * ONE identifier for both roles, deliberately. The AC requires a restored
   * queue to have "the same file identities", and it requires a retry not to
   * create a duplicate — those are the same guarantee, so giving them separate
   * ids would let the two drift apart. Generated ONCE when the file is
   * accepted, never on send: a key regenerated per attempt is not an
   * idempotency key at all, it is a fresh identity for every retry, which is
   * precisely the duplicate the column exists to prevent. */
  clientKey: string

  caseId: string
  file: File

  /** The filename, held separately from the File.
   *
   *  Redundant on paper. It is here because the queue must be able to RENDER a
   *  pending row — name, size, type — and reading those off the File is fine,
   *  but a Blob that failed to clone cleanly on some older browser would leave
   *  a row with no label. The list stays readable even when the payload is the
   *  thing that broke. */
  fileName: string
  contentType: string
  sizeBytes: number

  /** What kind of document this is, as the uploader chose before it queued
   *  (0045).
   *
   *  PERSISTED WITH THE FILE, which is the whole reason it is a field here
   *  rather than an argument to the send: the choice is made in the moment the
   *  file is picked, and the send can be days later on a different network. A
   *  kind held only in component state would be gone the moment the tab closed,
   *  and eight photos restored from disk would all commit as unclassified —
   *  which is precisely the state the picker exists to end.
   *
   *  Optional because entries written before this field existed are read back
   *  by the same code. `undefined` is filed as 'other' by the server's own
   *  normalisation, so an old entry commits rather than failing. */
  docKind?: string

  /** When the user added it. The retention window is measured from HERE, not
   *  from the last attempt: a file that has been retried for six days is six
   *  days old, and letting attempts refresh the clock would keep a permanently
   *  failing upload alive forever. */
  queuedAt: number

  /** Tie-breaker for files queued within the same millisecond.
   *
   *  Selecting eight photos from a picker enqueues them in a single tick, so
   *  Date.now() is IDENTICAL across all eight and cannot order them. Sorting on
   *  the timestamp alone therefore leaves their order to whatever IndexedDB
   *  happens to return, which reshuffles the pending list between reads and
   *  sends the user's photos in an order they did not choose.
   *
   *  A monotonic counter rather than performance.now(): it must survive being
   *  written to disk and compared against entries from a previous session, and
   *  performance.now() is measured from page load, so yesterday's entry and
   *  today's would be on unrelated scales. */
  sequence: number

  /** How many send attempts have been made. Drives the backoff, and is the
   *  number the inspectable queue shows when a user asks why something is
   *  still sitting there. */
  attempts: number

  /** The object key from a signing step that already succeeded, if one did.
   *
   *  Persisted because it is the expensive half of an interrupted upload. If
   *  the bytes reached the bucket and only the commit failed, re-signing would
   *  mint a SECOND key and re-send the bytes over the same bad link — so the
   *  key is kept and the retry resumes at the PUT. The idempotency key is what
   *  makes resuming safe: whichever key commits first wins, and the second
   *  commit returns the first one's row.
   *
   *  Undefined, never empty string, when no signing has succeeded — absence is
   *  "we have not got one", which is not the same as "we have a blank one". */
  objectKey?: string

  /** The last failure, in the user's words, or undefined when the entry has
   *  never failed. Undefined rather than '' so that "never failed" and "failed
   *  with an empty message" stay distinguishable. */
  lastError?: string
}

/* The open database, memoised.
 *
 * One connection per tab rather than one per operation: opening IndexedDB is
 * asynchronous and an open() per enqueue would serialise eight photos behind
 * eight handshakes. The promise is cached rather than the connection so that
 * concurrent callers during startup await the same open instead of racing to
 * create several. */
let dbPromise: Promise<IDBDatabase> | null = null

/** True when this environment can persist a queue at all.
 *
 *  Checked rather than assumed: Safari in private browsing historically exposed
 *  `indexedDB` and threw on open, and jsdom does not implement it. Every
 *  entry point below degrades to in-memory behaviour rather than throwing,
 *  because an uploader that crashes on a device without storage is worse than
 *  one that merely cannot survive a refresh there. */
export function queueStorageAvailable(): boolean {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null
  } catch {
    return false
  }
}

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise

  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE)) {
        // Keyed by clientKey, so putting the same entry twice UPDATES it rather
        // than queueing it again. That is what makes recordAttempt and
        // enqueue safe to call without first checking for an existing row.
        const store = db.createObjectStore(STORE, { keyPath: 'clientKey' })
        // The queue is always read for one case at a time — the file area is
        // mounted on a case. Without this index every read would scan every
        // queued upload across every case.
        store.createIndex(BY_CASE, 'caseId', { unique: false })
      }
    }

    request.onsuccess = () => {
      const db = request.result
      // A second tab upgrading the schema would block this connection forever.
      // Closing on versionchange lets that upgrade proceed; this tab reopens
      // on its next operation.
      db.onversionchange = () => {
        db.close()
        dbPromise = null
      }
      resolve(db)
    }

    request.onerror = () => reject(request.error ?? new Error('indexedDB open failed'))
    // Another tab holds an old version open. Reject rather than hang: the
    // caller falls back to a non-durable send, which still delivers the file.
    request.onblocked = () => reject(new Error('indexedDB blocked by another tab'))
  }).catch((error: unknown) => {
    // Do not memoise a failed open — a private-browsing session that is later
    // granted storage, or a transient error, should be retried rather than
    // remembered as permanently broken.
    dbPromise = null
    throw error
  })

  return dbPromise
}

/** Runs one transaction, resolving when it COMMITS rather than when the request
 *  succeeds.
 *
 *  The distinction is the reason this helper exists. An IDB request fires
 *  onsuccess while its transaction is still open; resolving there would report
 *  a queued file as saved before the write was durable, and a browser killed in
 *  that window loses exactly the file the user was told was safe. Waiting for
 *  `oncomplete` is what makes "queued" mean "on disk". */
function transact<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest | { result: T },
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode)
        const store = tx.objectStore(STORE)

        let value: T
        const request = run(store)
        if ('onsuccess' in request) {
          request.onsuccess = () => {
            value = request.result as T
          }
        } else {
          value = request.result
        }

        tx.oncomplete = () => resolve(value)
        tx.onerror = () => reject(tx.error ?? new Error('indexedDB transaction failed'))
        tx.onabort = () => reject(tx.error ?? new Error('indexedDB transaction aborted'))
      }),
  )
}

/** Mints an idempotency key (AD-18).
 *
 *  randomUUID where it exists, with a fallback because it is unavailable on
 *  insecure origins — and an Operations device on a warehouse LAN may well be
 *  hitting a plain-HTTP host. The fallback is not cryptographically strong and
 *  does not need to be: the key's job is to distinguish this upload from
 *  another one, not to be unguessable. The server's index is per CASE, so a
 *  collision would have to occur between two files on the same shipment. */
export function newClientKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

/* The next sequence number, seeded lazily from what is already on disk.
 *
 * Seeded rather than started at zero, because a new tab must not begin issuing
 * sequences that sort BEFORE files queued in an earlier session — that would
 * put yesterday's unsent photo behind today's. */
let nextSequence = 0
let sequenceSeeded = false

async function takeSequence(): Promise<number> {
  if (!sequenceSeeded) {
    sequenceSeeded = true
    try {
      const existing = await transact<QueuedUpload[]>('readonly', (store) =>
        store.getAll(),
      )
      for (const entry of existing ?? []) {
        // `?? 0` covers entries written before this field existed. They sort
        // first, which is correct: they are older than anything queued now.
        nextSequence = Math.max(nextSequence, (entry.sequence ?? 0) + 1)
      }
    } catch {
      // Unreadable store. Zero is a safe start — the worst case is that one
      // session's files tie with another's, which the timestamp then separates.
    }
  }
  return nextSequence++
}

/** Persists a file for sending. Resolves once the write has COMMITTED. */
export async function enqueue(
  caseId: string,
  file: File,
  clientKey = newClientKey(),
  docKind?: string,
): Promise<QueuedUpload> {
  const entry: QueuedUpload = {
    clientKey,
    caseId,
    file,
    fileName: file.name,
    contentType: file.type || 'application/octet-stream',
    sizeBytes: file.size,
    docKind,
    queuedAt: Date.now(),
    sequence: await takeSequence(),
    attempts: 0,
  }
  await transact('readwrite', (store) => store.put(entry))
  return entry
}

/** Queue order: when the user added it, then the tie-break for files added in
 *  the same millisecond.
 *
 *  One comparator for both reads, so the per-case list and the drain loop
 *  cannot disagree about which photo goes first. */
function byQueueOrder(a: QueuedUpload, b: QueuedUpload): number {
  if (a.queuedAt !== b.queuedAt) return a.queuedAt - b.queuedAt
  return (a.sequence ?? 0) - (b.sequence ?? 0)
}

/** Everything still queued for one case, oldest first.
 *
 *  Oldest first because the queue is a QUEUE: a user who shot eight photos
 *  expects the first one they took to send first, and out-of-order delivery on
 *  a flaky link makes the pending count jump around unreadably. */
export async function listForCase(caseId: string): Promise<QueuedUpload[]> {
  const entries = await transact<QueuedUpload[]>('readonly', (store) =>
    store.index(BY_CASE).getAll(caseId),
  )
  return (entries ?? []).sort(byQueueOrder)
}

/** Every queued upload, across all cases. Used by the drain loop, which sends
 *  on reconnect regardless of which case happens to be open — the AC requires
 *  files to re-send by themselves, not only while the user is looking at them. */
export async function listAll(): Promise<QueuedUpload[]> {
  const entries = await transact<QueuedUpload[]>('readonly', (store) => store.getAll())
  return (entries ?? []).sort(byQueueOrder)
}

/** Removes an entry, once its file is genuinely recorded on the server.
 *
 *  Called ONLY after a successful commit. Removing on a failed attempt is the
 *  bug this queue exists to prevent. */
export async function remove(clientKey: string): Promise<void> {
  await transact('readwrite', (store) => store.delete(clientKey))
}

/** Writes back an entry after an attempt: its attempt count, whatever object
 *  key the signing step won, and why it failed.
 *
 *  A put of the WHOLE entry rather than a partial update, because IndexedDB has
 *  no partial update and a read-modify-write here would race the drain loop. */
export async function recordAttempt(entry: QueuedUpload): Promise<void> {
  await transact('readwrite', (store) => store.put(entry))
}

/** Drops entries past the retention window, returning how many went.
 *
 *  Deliberately conservative: it deletes ONLY on the expiry test, never on a
 *  count limit and never on repeated failure. An upload that has failed forty
 *  times on a bad link is still the user's work, and the moment this function
 *  starts making judgements about which pending files are worth keeping is the
 *  moment the queue stops being something anybody can rely on. */
export async function sweepExpired(now = Date.now()): Promise<number> {
  const entries = await listAll()
  const stale = entries.filter((entry) => now - entry.queuedAt > QUEUE_RETENTION_MS)
  for (const entry of stale) {
    await remove(entry.clientKey)
  }
  return stale.length
}

/** Closes and forgets the connection. Tests only — production holds one
 *  connection for the life of the tab. */
export function resetQueueConnectionForTests(): void {
  void dbPromise?.then((db) => db.close()).catch(() => {})
  dbPromise = null
  // The sequence is re-seeded from disk on the next enqueue, which is what a
  // fresh tab does.
  nextSequence = 0
  sequenceSeeded = false
}
